import { createPortal } from 'react-dom';
import { cn } from '../../utils/classNames';
import useTooltip from '../../hooks/useTooltip';

const containerClasses = 'tooltip-container relative inline-flex';

const bubbleBaseClasses = `
  tooltip-bubble
  fixed z-tooltip pointer-events-none w-max
  transition-[opacity,transform] duration-150 ease-[cubic-bezier(0.4,0,0.2,1)]
`;

const bubbleHiddenClasses = 'opacity-0 scale-[0.85]';
const bubbleVisibleClasses = 'tooltip-visible opacity-100 scale-100';

const placementClasses = {
  top: '-translate-x-1/2 -translate-y-full origin-bottom',
  bottom: '-translate-x-1/2 origin-top',
  left: '-translate-x-full -translate-y-1/2 origin-right',
  right: '-translate-y-1/2 origin-left',
};

const contentClasses = `
  tooltip-content
  bg-content/92 text-white
  text-[calc(var(--spacing)*3.25)] font-medium leading-[1.4]
  py-1.5 px-3 rounded-md
  shadow-[0_4px_16px_rgba(0,0,0,0.18),0_1px_4px_rgba(0,0,0,0.12)]
  text-center max-w-65 break-words whitespace-normal
`;

const arrowBaseClasses = 'tooltip-arrow absolute w-0 h-0';

const arrowPlacementClasses = {
  top: 'bottom-[-6px] left-1/2 -translate-x-1/2 border-l-[calc(var(--spacing)*1.5)] border-r-[calc(var(--spacing)*1.5)] border-t-[calc(var(--spacing)*1.5)] border-l-transparent border-r-transparent border-t-content/92',
  bottom: 'top-[-6px] left-1/2 -translate-x-1/2 border-l-[calc(var(--spacing)*1.5)] border-r-[calc(var(--spacing)*1.5)] border-b-[calc(var(--spacing)*1.5)] border-l-transparent border-r-transparent border-b-content/92',
  left: 'right-[-6px] top-1/2 -translate-y-1/2 border-t-[calc(var(--spacing)*1.5)] border-b-[calc(var(--spacing)*1.5)] border-l-[calc(var(--spacing)*1.5)] border-t-transparent border-b-transparent border-l-content/92',
  right: 'left-[-6px] top-1/2 -translate-y-1/2 border-t-[calc(var(--spacing)*1.5)] border-b-[calc(var(--spacing)*1.5)] border-r-[calc(var(--spacing)*1.5)] border-t-transparent border-b-transparent border-r-content/92',
};

const Tooltip = ({
  children,
  title = '',
  placement = 'top',
  arrow = true,
  enterDelay = 200,
  leaveDelay = 100,
}) => {
  const { isVisible, position, containerProps } = useTooltip({ enterDelay, leaveDelay, placement });

  if (!title) {
    return <>{children}</>;
  }

  const placementClass = placementClasses[placement] ?? placementClasses.top;
  const arrowPlacementClass = arrowPlacementClasses[placement] ?? arrowPlacementClasses.top;

  const bubbleClasses = cn(
    bubbleBaseClasses,
    `tooltip-${placement}`,
    placementClass,
    isVisible ? bubbleVisibleClasses : bubbleHiddenClasses,
  );

  const bubble = (
    <span
      className={bubbleClasses}
      role="tooltip"
      style={{ top: position.top, left: position.left }}
    >
      <span className={contentClasses}>{title}</span>
      {arrow && <span className={cn(arrowBaseClasses, arrowPlacementClass)} />}
    </span>
  );

  return (
    <span className={containerClasses} {...containerProps}>
      {children}
      {createPortal(bubble, document.body)}
    </span>
  );
};

export default Tooltip;
